import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Typography, Paper, Box, Button, List, ListItem, ListItemText, ListItemAvatar,
    Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tabs, Tab, TextField, FormControl, InputLabel, Select, MenuItem,
    Card, CardContent, Divider, Chip, ListItemIcon, Tooltip, Alert, Autocomplete, CircularProgress, Grow
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import CancelIcon from '@mui/icons-material/Cancel';
import FlagIcon from '@mui/icons-material/Flag';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import StoreIcon from '@mui/icons-material/Store';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import DevicesIcon from '@mui/icons-material/Devices';
import InfoIcon from '@mui/icons-material/Info';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';

import axios from 'axios';
import API_URL from '../../config';
import Loading from '../../components/Loading';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && (
                <Box sx={{ py: 3, px: { xs: 1, sm: 2, md: 3 }, overflowX: 'hidden' }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function SoftwareLicenseDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [license, setLicense] = useState(null);
    const [renewals, setRenewals] = useState([]);
    const [files, setFiles] = useState([]);
    const [logs, setLogs] = useState([]);
    const [users, setUsers] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [countries, setCountries] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    // Chat
    const [conversations, setConversations] = useState([]);
    const [newMessage, setNewMessage] = useState('');

    // Edit Mode
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState({});

    // Renewals
    const [openRenewal, setOpenRenewal] = useState(false);
    const [newRenewal, setNewRenewal] = useState({ renewal_date: '', start_date: '', end_date: '', cost: '', remarks: '' });

    // Drag and Drop
    const [dragging, setDragging] = useState(false);

    // AD Search State
    const [adUsers, setAdUsers] = useState([]);
    const [adLoading, setAdLoading] = useState(false);

    const handleADSearch = async (query) => {
        if (!query || query.length < 3) {
            setAdUsers([]);
            return;
        }
        setAdLoading(true);
        try {
            const res = await axios.get(`${API_URL}/tickets/search-ad-users?q=${query}`, { headers });
            setAdUsers(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setAdLoading(false);
        }
    };

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchLicense();
        fetchRenewals();
        fetchFiles();
        fetchLogs();
        fetchSetupData();
        fetchConversations();
    }, [id]);

    const fetchConversations = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}/conversations`, { headers });
            setConversations(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchLogs = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}/logs`, { headers });
            setLogs(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchSetupData = async () => {
        try {
            const [resUsers, resDivs, resCountries, resSuppliers] = await Promise.all([
                axios.get(`${API_URL}/setup/users`, { headers }),
                axios.get(`${API_URL}/setup/divisions`, { headers }),
                axios.get(`${API_URL}/setup/countries`, { headers }),
                axios.get(`${API_URL}/it-suppliers`, { headers })
            ]);
            setUsers(resUsers.data);
            setDivisions(resDivs.data);
            setCountries(resCountries.data);
            setSuppliers(resSuppliers.data);
        } catch (e) { console.error(e); }
    };

    const fetchLicense = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}`, { headers });
            setLicense(res.data);
            setEditData(res.data);
            setLoading(false);
        } catch (e) { console.error(e); setLoading(false); }
    };

    const fetchRenewals = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}/renewals`, { headers });
            setRenewals(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchFiles = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}/files`, { headers });
            setFiles(res.data);
        } catch (e) { console.error(e); }
    };

    const handleUpdate = async () => {
        try {
            await axios.put(`${API_URL}/software-licenses/${id}`, editData, { headers });
            await fetchLicense();
            await fetchLogs(); // Validation: Refresh logs after update
            setEditMode(false);
        } catch (e) { console.error(e); }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this license?")) return;
        try {
            await axios.delete(`${API_URL}/software-licenses/${id}`, { headers });
            navigate('/software-licenses');
        } catch (e) { console.error(e); }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            const formData = new FormData();
            formData.append('file', file);
            try {
                await axios.post(`${API_URL}/software-licenses/${id}/files`, formData, { headers });
                fetchFiles();
            } catch (e) {
                console.error(e);
                alert("File upload failed: " + (e.response?.data?.error || e.message));
            }
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`${API_URL}/software-licenses/${id}/files`, formData, { headers });
            fetchFiles();
        } catch (e) {
            console.error(e);
            alert("File upload failed: " + (e.response?.data?.error || e.message));
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;
        try {
            await axios.post(`${API_URL}/software-licenses/${id}/conversations`, { message: newMessage }, { headers });
            setNewMessage('');
            fetchConversations();
        } catch (e) { console.error(e); }
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm("Delete this file?")) return;
        try {
            await axios.delete(`${API_URL}/software-licenses/${id}/files/${fileId}`, { headers });
            fetchFiles();
        } catch (e) { console.error(e); }
    };

    const handleAddRenewal = async () => {
        try {
            await axios.post(`${API_URL}/software-licenses/${id}/renewals`, newRenewal, { headers });
            setOpenRenewal(false);
            setNewRenewal({ renewal_date: '', start_date: '', end_date: '', cost: '', remarks: '' });
            fetchRenewals();
        } catch (e) { console.error(e); }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return '#4caf50'; // success
            case 'Expired': return '#f44336'; // error
            case 'Pending Renewal': return '#ff9800'; // warning
            default: return '#9e9e9e'; // default
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    if (loading) return <Loading />;
    if (!license) return <Typography>License not found</Typography>;

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in={true} timeout={500}>
            {/* Header Section */}
            <Paper elevation={0} sx={{ mt: 2, p: { xs: 2, md: 4 }, mb: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)' }}>
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', zIndex: 0 }} />
                <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', zIndex: 0 }} />
                
                <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/software-licenses')}
                            sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', textTransform: 'none', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
                        >
                            Back to Licenses
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid rgba(255,255,255,0.2)', width: 52, height: 52, flexShrink: 0, fontSize: '1.3rem', fontWeight: 700, boxShadow: '0 6px 18px rgba(0,0,0,0.2)' }}>
                                {license.name ? license.name.charAt(0).toUpperCase() : <DevicesIcon />}
                            </Avatar>
                            <Typography variant="h5" color="white" fontWeight="900" sx={{ letterSpacing: 0.3, lineHeight: 1.3, wordBreak: 'break-word' }}>{license.name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 0.5 }}>
                            <Chip label={license.status} icon={<InfoIcon style={{ color: 'white' }} />} size="small" sx={{ bgcolor: `${getStatusColor(license.status)}`, color: 'white', fontWeight: 700, borderRadius: 1.5 }} />
                            {license.department_name && (
                               <Chip icon={<BusinessIcon fontSize="small" style={{ color: 'rgba(255,255,255,0.7)' }}/>} label={license.department_name} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: 1.5 }} />
                            )}
                            {license.country_name && (
                                <Chip icon={<FlagIcon fontSize="small" style={{ color: 'rgba(255,255,255,0.7)' }}/>} label={license.country_name} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: 1.5 }} />
                            )}
                            {license.supplier_name && (
                               <Chip icon={<StoreIcon fontSize="small" style={{ color: 'rgba(255,255,255,0.7)' }}/>} label={license.supplier_name} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: 1.5 }} />
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={handleDelete}
                            variant="contained"
                            startIcon={<DeleteIcon />}
                            sx={{ bgcolor: 'rgba(244,67,54,0.85)', color: 'white', fontWeight: 'bold', px: 2.5, borderRadius: 2, '&:hover': { bgcolor: '#d32f2f', transform: 'translateY(-2px)' }, transition: 'all 0.2s' }}
                        >
                            Delete
                        </Button>
                        {!editMode ? (
                            <Button
                                onClick={() => setEditMode(true)}
                                variant="contained"
                                startIcon={<EditIcon />}
                                sx={{ bgcolor: 'white', color: '#1e3c72', fontWeight: 'bold', px: 2.5, borderRadius: 2, boxShadow: '0 4px 14px rgba(0,0,0,0.2)', '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-2px)' }, transition: 'all 0.2s' }}
                            >
                                Edit
                            </Button>
                        ) : (
                            <>
                                <Button
                                    onClick={handleUpdate}
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 'bold', px: 2.5, borderRadius: 2, '&:hover': { bgcolor: '#388e3c' } }}
                                >
                                    Save
                                </Button>
                                <Button
                                    onClick={() => { setEditMode(false); setEditData(license); }}
                                    variant="contained"
                                    startIcon={<CancelIcon />}
                                    sx={{ bgcolor: '#f44336', color: 'white', fontWeight: 'bold', px: 2.5, borderRadius: 2, '&:hover': { bgcolor: '#d32f2f' } }}
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>
            </Grow>

            <Grow in={true} timeout={800}>
            <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #f0f0f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', mb: 4, bgcolor: 'white' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }} sx={{ '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 64, fontSize: '0.95rem' }, '& .Mui-selected': { color: 'primary.main' } }}>
                        <Tab icon={<InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="General Info" />
                        <Tab icon={<HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="Renewals" />
                        <Tab icon={<ChatIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="Conversations" />
                        <Tab icon={<AttachFileIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="Attachments" />
                        <Tab icon={<AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="Audit Log" />
                    </Tabs>
                </Box>

                {/* General Info Panel */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        {editMode ? (
                            <Grid item xs={12}>
                                <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4 }}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom sx={{ mb: 3 }}>Edit Information</Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField label="Name" fullWidth value={editData.name || ''} onChange={(e) => setEditData({...editData, name: e.target.value})} variant="outlined" />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel>Status</InputLabel>
                                                    <Select value={editData.status || 'Active'} onChange={(e) => setEditData({...editData, status: e.target.value})} label="Status">
                                                        <MenuItem value="Active">Active</MenuItem>
                                                        <MenuItem value="Expired">Expired</MenuItem>
                                                        <MenuItem value="Pending Renewal">Pending Renewal</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Autocomplete
                                                    freeSolo
                                                    options={adUsers}
                                                    loading={adLoading}
                                                    getOptionLabel={(option) => {
                                                        if (typeof option === 'string') return option;
                                                        return option.displayName || option.name || '';
                                                    }}
                                                    onInputChange={(event, newInputValue) => { handleADSearch(newInputValue); }}
                                                    filterOptions={(x) => x} 
                                                    value={editData.requested_by_name ? { displayName: editData.requested_by_name, mail: editData.requested_by_email } : null}
                                                    onChange={(e, newValue) => {
                                                        if (typeof newValue === 'string') {
                                                            setEditData({ ...editData, requested_by_name: newValue, requested_by_email: '', requested_by_id: null });
                                                        } else if (newValue) {
                                                            const name = newValue.displayName || newValue.username;
                                                            const email = newValue.mail || newValue.email || newValue.userPrincipalName;
                                                            setEditData({ ...editData, requested_by_name: name, requested_by_email: email, requested_by_id: null });
                                                        } else {
                                                            setEditData({ ...editData, requested_by_name: '', requested_by_email: '', requested_by_id: null });
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Requested By" 
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                endAdornment: (
                                                                    <React.Fragment>
                                                                        {adLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                        {params.InputProps.endAdornment}
                                                                    </React.Fragment>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                    renderOption={(props, option) => (
                                                        <li {...props}>
                                                            <Box>
                                                                <Typography variant="body1">{option.displayName || option.name}</Typography>
                                                                {(option.mail || option.userPrincipalName) && <Typography variant="caption" color="textSecondary">{option.mail || option.userPrincipalName}</Typography>}
                                                            </Box>
                                                        </li>
                                                    )}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel>Department</InputLabel>
                                                    <Select value={editData.department_id || ''} onChange={(e) => setEditData({...editData, department_id: e.target.value})} label="Department">
                                                        <MenuItem value=""><em>None</em></MenuItem>
                                                        {divisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel>Country</InputLabel>
                                                    <Select value={editData.country_id || ''} onChange={(e) => setEditData({...editData, country_id: e.target.value})} label="Country">
                                                        <MenuItem value=""><em>None</em></MenuItem>
                                                        {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel>Supplier</InputLabel>
                                                    <Select value={editData.supplier_id || ''} onChange={(e) => setEditData({...editData, supplier_id: e.target.value})} label="Supplier">
                                                        <MenuItem value=""><em>None</em></MenuItem>
                                                        {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <TextField label="Description" fullWidth multiline rows={4} value={editData.description || ''} onChange={(e) => setEditData({...editData, description: e.target.value})} variant="outlined" />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={12} md={8}>
                                    <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom sx={{ mb: 3 }}>Description</Typography>
                                            <Typography variant="body1" sx={{ color: license.description ? 'text.primary' : 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                                                {license.description || 'No description provided for this software license.'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom sx={{ mb: 3 }}>Details</Typography>
                                            <List sx={{ p: 0 }}>
                                                <ListItem sx={{ px: 0, py: 1.5 }}>
                                                    <ListItemIcon>
                                                        <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 44, height: 44, boxShadow: '0 2px 10px rgba(59,130,246,0.15)' }}>
                                                            <PersonIcon />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={<Typography variant="caption" color="text.secondary" fontWeight="bold" letterSpacing={0.5} textTransform="uppercase">Requested By</Typography>} 
                                                        secondary={<Typography variant="body1" color="text.primary" fontWeight="medium">{license.requested_by_name || '-'}</Typography>} 
                                                    />
                                                </ListItem>
                                                <Divider variant="inset" />
                                                <ListItem sx={{ px: 0, py: 1.5 }}>
                                                    <ListItemIcon>
                                                        <Avatar sx={{ bgcolor: '#e8f5e9', color: '#4caf50', width: 44, height: 44, boxShadow: '0 2px 10px rgba(76,175,80,0.15)' }}>
                                                            <StoreIcon />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={<Typography variant="caption" color="text.secondary" fontWeight="bold" letterSpacing={0.5} textTransform="uppercase">Supplier</Typography>} 
                                                        secondary={<Typography variant="body1" color="text.primary" fontWeight="medium">{license.supplier_name || '-'}</Typography>} 
                                                    />
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </TabPanel>

                {/* Renewals Panel */}
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">Renewals ({renewals.length})</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenRenewal(true)} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)', textTransform: 'none' }}>
                            Add Renewal
                        </Button>
                    </Box>

                    {renewals.length === 0 ? (
                         <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', border: '2px dashed #e2e8f0', borderRadius: 4 }}>
                            <HistoryIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                            <Typography variant="h6" color="#64748b" fontWeight="bold">No Renewals found</Typography>
                            <Typography color="text.secondary">Create and manage renewal records for this license.</Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={3}>
                            {renewals.map(r => (
                                <Grid item xs={12} sm={6} md={4} key={r.id}>
                                    <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 12px 28px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' } }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar sx={{ bgcolor: '#fff3e0', color: '#ff9800', fontWeight: 'bold' }}>
                                                        <HistoryIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">{new Date(r.renewal_date).toLocaleDateString()}</Typography>
                                                        <Typography variant="body2" color="text.secondary">Cost: {r.cost ? `$${r.cost}` : 'N/A'}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Divider sx={{ my: 1.5 }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Typography variant="body2" color="text.secondary">Start: <b>{r.start_date ? new Date(r.start_date).toLocaleDateString() : '-'}</b></Typography>
                                                <Typography variant="body2" color="text.secondary">End: <b>{r.end_date ? new Date(r.end_date).toLocaleDateString() : '-'}</b></Typography>
                                            </Box>
                                            {r.remarks && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', bgcolor: '#f8fafc', p: 1, borderRadius: 1 }}>{r.remarks}</Typography>}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </TabPanel>

                {/* Conversations Panel */}
                <TabPanel value={tabValue} index={2}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '600px', border: '1px solid #f1f5f9', borderRadius: 4, bgcolor: 'white', overflow: 'hidden' }}>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {conversations.length === 0 ? (
                                <Box sx={{ m: 'auto', textAlign: 'center' }}>
                                    <ChatIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                    <Typography variant="h6" color="#64748b" fontWeight="bold">No conversations yet</Typography>
                                    <Typography color="text.secondary">Start the conversation below.</Typography>
                                </Box>
                            ) : (
                                conversations.map(c => (
                                    <Paper key={c.id} elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', maxWidth: '80%', alignSelf: 'flex-start', bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" color="primary.main">{c.created_by_name || 'User'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{new Date(c.created_at).toLocaleString()}</Typography>
                                        </Box>
                                        <Typography variant="body1">{c.message}</Typography>
                                    </Paper>
                                ))
                            )}
                        </Box>
                        <Divider />
                        <Box sx={{ p: 2, display: 'flex', gap: 1, bgcolor: 'white' }}>
                            <TextField fullWidth placeholder="Type a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} variant="outlined"  InputProps={{ sx: { borderRadius: 3 } }}/>
                            <Button variant="contained" endIcon={<SendIcon />} onClick={handleSendMessage} disabled={!newMessage.trim()} sx={{ borderRadius: 3, px: 4, background: newMessage.trim() ? 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)' : undefined }}>
                                Send
                            </Button>
                        </Box>
                    </Box>
                </TabPanel>

                {/* Attachments Panel */}
                <TabPanel value={tabValue} index={3}>
                     <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">Files ({files.length})</Typography>
                        <Button component="label" variant="contained" startIcon={<CloudUploadIcon />} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)', textTransform: 'none' }}>
                            Upload File
                            <input type="file" hidden onChange={handleFileUpload} />
                        </Button>
                    </Box>

                    <Paper
                        elevation={0}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        sx={{
                            p: 3, mb: 4, textAlign: 'center', border: '2px dashed',
                            borderColor: dragging ? 'primary.main' : '#e2e8f0',
                            bgcolor: dragging ? 'rgba(25, 118, 210, 0.05)' : '#f8fafc',
                            borderRadius: 4, transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <CloudUploadIcon sx={{ fontSize: 48, color: dragging ? 'primary.main' : '#cbd5e1', mb: 1 }} />
                        <Typography variant="body1" color={dragging ? "primary.main" : "text.secondary"} fontWeight="bold">
                            Drag & Drop files here, or click upload button.
                        </Typography>
                    </Paper>
                    
                    <Grid container spacing={2}>
                        {files.map(f => (
                            <Grid item xs={12} sm={6} md={4} key={f.id}>
                                <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, display: 'flex', alignItems: 'center', p: 2, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 20px rgba(0,0,0,0.06)' } }}>
                                    <Avatar sx={{ bgcolor: 'error.light', color: 'white', mr: 2 }}>
                                        <DescriptionIcon />
                                    </Avatar>
                                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                        <Typography variant="subtitle2" noWrap fontWeight="bold" title={f.filename}>{f.filename}</Typography>
                                        <Typography variant="caption" color="text.secondary">{new Date(f.created_at).toLocaleDateString()}</Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => window.open(`${API_URL}/files/download/${f.file_path}`)} sx={{ color: 'primary.main' }}>
                                        <DownloadIcon fontSize="small"/>
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteFile(f.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </TabPanel>

                {/* Audit Log Panel */}
                <TabPanel value={tabValue} index={4}>
                    <Typography variant="h6" fontWeight="bold" color="text.primary" mb={3}>Audit Logs</Typography>
                    {logs.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', border: '2px dashed #e2e8f0', borderRadius: 4 }}>
                            <AssessmentIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                            <Typography variant="h6" color="#64748b" fontWeight="bold">No Logs found</Typography>
                            <Typography color="text.secondary">History of changes will appear here.</Typography>
                        </Paper>
                    ) : (
                        <List sx={{ bgcolor: 'white', border: '1px solid #f1f5f9', borderRadius: 4, p: 0 }}>
                            {logs.map((log, index) => (
                                <React.Fragment key={log.id}>
                                    <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: '#f1f5f9', color: '#64748b' }}>
                                                <AssessmentIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                                                    {log.action}
                                                </Typography>
                                            }
                                            secondary={
                                                <>
                                                    <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block', mt: 0.5 }}>
                                                        {log.details}
                                                    </Typography>
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                        By {log.created_by_name} on {new Date(log.created_at).toLocaleString()}
                                                    </Typography>
                                                </>
                                            }
                                        />
                                    </ListItem>
                                    {index < logs.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </TabPanel>

            </Paper>
            </Grow>

            {/* Dialog for New Renewal */}
            <Dialog open={openRenewal} onClose={() => setOpenRenewal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    New Renewal
                    <CloseIcon onClick={() => setOpenRenewal(false)} sx={{ cursor: 'pointer' }} />
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', mt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField label="Renewal Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newRenewal.renewal_date} onChange={(e) => setNewRenewal({ ...newRenewal, renewal_date: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newRenewal.start_date} onChange={(e) => setNewRenewal({ ...newRenewal, start_date: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="End Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newRenewal.end_date} onChange={(e) => setNewRenewal({ ...newRenewal, end_date: e.target.value })} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label="Cost" type="number" fullWidth value={newRenewal.cost} onChange={(e) => setNewRenewal({ ...newRenewal, cost: e.target.value })} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label="Remarks" fullWidth multiline rows={2} value={newRenewal.remarks} onChange={(e) => setNewRenewal({ ...newRenewal, remarks: e.target.value })} />
                            </Grid>
                        </Grid>
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setOpenRenewal(false)} sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Cancel</Button>
                    <Button onClick={handleAddRenewal} variant="contained" disabled={!newRenewal.renewal_date} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)', px: 3 }}>Create Renewal</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SoftwareLicenseDetails;
